/**
 * Barra fina no topo da página, para trabalho que roda em segundo plano.
 *
 * A atualização é feita direto no DOM, sem estado do React, pelo mesmo motivo do `updateProgress`
 * do sync: quem hospeda a barra é o `Container`, que envolve o app inteiro — um `setState` por
 * imagem re-renderizaria toda a árvore centenas de vezes para mexer em três pixels.
 *
 * Só a largura e a opacidade mudam: as duas são propriedades que o navegador anima sem recalcular
 * layout do resto da página.
 */

export const TOP_PROGRESS_TRILHO_ID = 'top-progress-trilho';
export const TOP_PROGRESS_BARRA_ID = 'top-progress-barra';

/** Mesma cadência do progresso do sync: mais que isso é repintura que ninguém enxerga. */
const THROTTLE_MS = 150;

/** Tempo do fade final — o mesmo da transição de opacidade no estilo. */
const SAIDA_MS = 400;

let ultimaAtualizacao = 0;
let saida: ReturnType<typeof setTimeout> | null = null;

/**
 * Nós em cache: `getElementById` a cada avanço seria uma busca no documento inteiro no meio de um
 * trabalho que já está disputando o aparelho. O cache é descartado se o nó sair da árvore.
 */
let trilhoCache: HTMLElement | null = null;
let barraCache: HTMLElement | null = null;

const elemento = (id: string, cache: HTMLElement | null): HTMLElement | null => {
  if (cache?.isConnected) return cache;
  return typeof document === 'undefined' ? null : document.getElementById(id);
};

const getTrilho = () => (trilhoCache = elemento(TOP_PROGRESS_TRILHO_ID, trilhoCache));
const getBarra = () => (barraCache = elemento(TOP_PROGRESS_BARRA_ID, barraCache));

const definirLargura = (percentual: number) => {
  const barra = getBarra();
  if (barra) barra.style.width = `${Math.max(0, Math.min(100, percentual))}%`;
};

/** Mostra a barra zerada. Chamar quando o trabalho começa. */
export const iniciarProgressoTopo = () => {
  const trilho = getTrilho();
  if (!trilho) return;

  if (saida) {
    clearTimeout(saida);
    saida = null;
  }

  // Sem transição na volta ao zero: senão a barra "desanda" da direita para a esquerda à vista.
  const barra = getBarra();
  if (barra) {
    barra.style.transition = 'none';
    barra.style.width = '0%';
    // Força o navegador a aplicar o zero antes de religar a transição.
    void barra.offsetWidth;
    barra.style.transition = '';
  }

  trilho.style.visibility = 'visible';
  trilho.style.opacity = '1';
  ultimaAtualizacao = 0;
};

export const atualizarProgressoTopo = (feito: number, total: number) => {
  const agora = Date.now();
  if (agora - ultimaAtualizacao < THROTTLE_MS) return;
  ultimaAtualizacao = agora;

  definirLargura(total > 0 ? (feito / total) * 100 : 0);
};

/** Completa a barra e some com ela. */
export const finalizarProgressoTopo = () => {
  const trilho = getTrilho();
  if (!trilho) return;

  definirLargura(100);
  trilho.style.opacity = '0';

  // `visibility` só depois do fade, senão a barra sumiria cheia, de uma vez.
  if (saida) clearTimeout(saida);
  saida = setTimeout(() => {
    trilho.style.visibility = 'hidden';
    saida = null;
  }, SAIDA_MS);
};
